/**
 * Quote Piece
 * Artwork piece
 */

/**
 * @section Template
 */

<template>
<div :id="`piece-${pieceNumber}`">
  <fieldset class="form-order">
    <legend class="form-order-title">Project Specifications</legend>
      <div class="form-pair form-field" :class="{ 'is-errored': !validate('name') }">
          <label for="project-name" title="How would you describe this project?">
              Project Name
          </label>
          <input @blur="dirtify('name')" autocapitalize="words" class="form-letterbox" id="project-name" type="text" v-model="project.name">
          <div class="form-error" v-if="!validate('name')">Give this project a name</div>
      </div>
      <label :for="`${fieldPrefix}-referral`" class="mb05 mt3">How did you hear about Mama? <br /></label>
      <ul class="form-checklist br nl">
          <li class="bt" v-for="(referral, r) in referrals" :key="referral">
              <input @change="makeReferralStandard" @focus="dirtify('referral')" :value="referral" type="radio" class="form-check" :id="`${fieldPrefix}-referral-${r}`" v-model="project.referralSource">
              <label :for="`${fieldPrefix}-referral-${r}`" :class="{ 'bb': r == 3 }">
                  {{ referral }}
              </label>
          </li>
      </ul>
      <div class="form-pair form-field bt">
          <label :for="`${fieldPrefix}-referral-source-other`">Other</label>
          <input @blur="dirtify('referral')" @keyup="makeReferralCustom" class="form-letterbox" :id="`${fieldPrefix}-referral-source-other`" type="text" v-model="project.referralSourceOther">
      </div>
      <div class="form-error" v-if="!validate('product')">
          Please select at least one product type, or describe what you’d like to print.
      </div>
      <label :for="`${fieldPrefix}-notes`" class="mb05 mt3">Product: <br /></label>
      <ul class="form-checklist br nl bb">
          <li class="bt" v-for="(product, p) in products" :key="product.id">
              <input @focus="dirtify('product')" :value="product.id" type="radio" class="form-check" :id="`${fieldPrefix}-product-${p}`" number v-model="piece.product">
              <label :for="`${fieldPrefix}-product-${p}`" :class="{ 'bb': p == 5 }">
                  {{ product.name }}
              </label>
          </li>
      </ul>
      <div class="form-error" v-if="!validate('product')">
        Please select at least one product type, or describe what you’d like to print.
      </div>
    <div class="mt3">
      <label :for="`${fieldPrefix}-notes`" class="mb05">Tell us about your job: <br />
          <small class="tcg">
            (Things we’d like to know… quantity, print and finishing process(es), paper, ink colors, would you like us to suggest any of the above, etc.)
          </small>
      </label>
      <textarea :id="`${fieldPrefix}-notes`" v-model="piece.notes"></textarea>
    </div>
    <fieldset class="form-group">
      <h3 class="tca mt1 pb05">Upload Files</h3>
      <div class="has-dropzone">
        <div class="dropzone dropzone--fake">
          <div
            v-for="artwork in initialArtworks"
            :key="artwork.id"
            :data-fingerprint="artwork.id"
            class="dz-preview dz-preview dz-processing dz-image-preview dz-complete">
            <div class="dz-image file" :class="`file--${getExtension(artwork.thumbnail)}`">
              <img :src="artwork.thumbnail" v-if="isRaster(artwork.thumbnail)">
            </div>
            <div class="dz-details">
              <div class="dz-size"><span>{{ artwork.size | inMegabytes }}</span></div>
              <div class="dz-filename"><span>{{ dzRenameFilename(artwork.name) }}</span></div>
            </div>
            <div class="dz-progress"></div>
            <a class="dz-remove" @click="removeArtworkHandler(artwork.id)">Remove File</a>
          </div>
        </div>
        <v-dropzone
          :id="`dz-${pieceNumber}`"
          url="/quote/upload"
          method="PUT"
          :useCustomDropzoneOptions="true"
          :dropzoneOptions="dzOptions"></v-dropzone>
      </div>
      <input name="artworks" type="hidden" :value="this.piece.artworks">
    </fieldset>
  </fieldset>
</div>
</template>

/**
 * @section Logic
 */

<script>
import axios from 'axios';
import Dropzone from 'vue2-dropzone';

export default {
  created() {
    this.piece.artworks = this.piece.artworks || [];
    this.initialArtworks = this.piece.artworks.slice(0);
  },
  data() {
    return {
      dirtyFields: [],
      dzOptions: {
        addRemoveLinks: true,
        dictDefaultMessage: 'Drag and drop files here to upload',
        headers: { 'X-CSRF-Token': this.token },
        method: 'PUT',
        removedfile: this.dzRemovedFile,
        renameFilename: this.dzRenameFilename,
        success: this.dzSuccess,
        url: '/quote/upload',
      },
      initialArtworks: [{}],
    };
  },
  props: {
    contact: Object,
    isSubmitted: Boolean,
    piece: Object,
    pieceNumber: Number,
    printTypes: Array,
    project: Object,
    referralTypes: Array,
    removePiece: Function,
    token: String,
  },
  computed: {
    fieldPrefix() {
      return `piece-${this.pieceNumber}`;
    },

    isInvalid() {
      return this.addressInvalid ||
        this.detailsInvalid ||
        this.captchaInvalid ||
        this.contactInvalid;
    },

    products() {
      if (!this.printTypes.length) return [];
      return this.printTypes.filter(item => item.product_or_process.toLowerCase() === 'product');
    },
    referrals() {
      if (!this.referralTypes.length) return [];
      return this.referralTypes;
    },
  },

  methods: {
    dirtify(key) {
      if (this.dirtyFields.indexOf(key) === -1)
        this.dirtyFields.push(key);
    },

    dzRemovedFile(file) {
      const artworkID = file.previewElement.dataset.fingerprint;
      this.removeArtworkFile(artworkID);
    },

    dzRenameFilename(name) {
      if (!this.contact.lastName) return name;
      return `${[this.contact.lastName, this.contact.firstName].join('_').toLowerCase()}__${name}`;
    },

    dzSuccess(file) {
      const data = JSON.parse(file.xhr.responseText);
      this.piece.artworks.push({
        createdAt: Date.now(),
        id: data.id,
        name: file.name,
        size: file.size,
        thumbnail: data.image.url,
        type: file.type,
      });
      file.previewElement.dataset.fingerprint = data.id;
      const ext = this.getExtension(file.name);
      file.previewElement.querySelector('.dz-image').classList.add('file', `file--${ext}`);
    },

    getExtension(filename) {
      return filename.toLowerCase().match(/[^\.][0-9a-z]+$/)[0];
    },

    isDirty(key) {
      return this.dirtyFields.indexOf(key) !== -1;
    },

    isRaster(filename) {
      return ['jpg', 'jpeg', 'gif', 'png'].indexOf(this.getExtension(filename)) != -1;
    },

    makeReferralCustom() {
      this.project.referralSource = null;
    },

    makeReferralStandard() {
      this.project.referralSourceOther = '';
    },

    removeArtworkFile(artworkID) {
      axios.delete(
        `/quote/remove/${artworkID}`,
        { headers: { 'X-CSRF-Token': this.token } },
      ).then(() => {
        document.querySelector(`[data-fingerprint="${artworkID}"`).remove();
        const index = this.piece.artworks.findIndex(({ id }) => id === artworkID);
        console.log(artworkID);
        this.piece.artworks.splice(index, 1);
      }).catch(() => {
        console.log('There was an error deleting the file.');
      });
    },

    removeArtworkHandler(artworkID) {
      this.removeArtworkFile(artworkID);
      const index = this.initialArtworks.findIndex(artwork => artwork.id === artworkID);
      this.initialArtworks.splice(index, 1);
    },

    validate(type) {
      switch(type) {
        case 'artwork':
          return this.piece.artworkStatus === 'noArtwork' || this.piece.artworks.length !== 0;
          break;

        case 'name':
          return this.project.name && this.project.name.length !== 0 || !this.isDirty(type);
          break;

        case 'product':
          return this.piece.product && typeof this.piece.product === 'number' ||
            !this.isDirty(type);
          break;
      }
      return true;
    },
  },

  components: {
    'v-dropzone': Dropzone,
  },
};
</script>



// WEBPACK FOOTER //
// components/QuotePiece.vue